import { useMemo } from 'react';
import { Filter, GripVertical, Plus } from 'lucide-react';
import type { IssueListItem } from '../../services/issueService';
import type { ProjectMember } from '../../services/projectService';
import type { BacklogFilter } from './BacklogView';
import { PRIORITY_CONFIG } from '../board/boardUtils';
import { resolveIssueTypeConfig } from '../../constants/issueTypeConfig';
import { usePanelContext } from '../../contexts/PanelContext';

interface Props {
  issues: IssueListItem[];
  isLoading: boolean;
  filter: BacklogFilter;
  onFilterChange: (filter: BacklogFilter) => void;
  selectedIds: Set<string>;
  onToggleSelect: (issueId: string) => void;
  onToggleSelectAll: () => void;
  members: ProjectMember[];
  onCreateIssue?: () => void;
}

const FALLBACK_ISSUE_TYPES = ['Task', 'Bug', 'Story', 'Epic', 'Subtask'];
const PRIORITIES = ['BLOCKER', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW'] as const;

export default function BacklogPanel({
  issues,
  isLoading,
  filter,
  onFilterChange,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  members,
  onCreateIssue,
}: Props) {
  const { openDetailPanel } = usePanelContext();
  const allSelected = issues.length > 0 && selectedIds.size === issues.length;

  const totalPoints = useMemo(
    () => issues.reduce((sum, i) => sum + (i.storyPoints ?? 0), 0),
    [issues],
  );

  const handleDragStart = (e: React.DragEvent, issue: IssueListItem) => {
    e.dataTransfer.setData(
      'application/json',
      JSON.stringify({ issueId: issue.id, from: 'backlog' }),
    );
    e.dataTransfer.effectAllowed = 'move';
  };

  const toggleType = (type: string) => {
    const next = filter.issueType.includes(type)
      ? filter.issueType.filter((t) => t !== type)
      : [...filter.issueType, type];
    onFilterChange({ ...filter, issueType: next });
  };

  const togglePriority = (p: string) => {
    const next = filter.priority.includes(p)
      ? filter.priority.filter((x) => x !== p)
      : [...filter.priority, p];
    onFilterChange({ ...filter, priority: next });
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-lg border border-gray-200">
      {/* Header */}
      <div className="px-3 py-2.5 border-b border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-800">
            백로그
            <span className="ml-1.5 text-sm font-normal text-gray-400">
              {issues.length}건 · {totalPoints}pt
            </span>
          </h3>
          <div className="flex items-center gap-1.5">
            {onCreateIssue && (
              <button
                onClick={onCreateIssue}
                className="flex items-center gap-0.5 text-sm text-blue-600 hover:text-blue-800"
              >
                <Plus className="w-3.5 h-3.5" />
                작업 생성
              </button>
            )}
            <Filter className="w-4 h-4 text-gray-400" />
          </div>
        </div>

        {/* Filters */}
        <div className="space-y-1.5">
          {/* Issue Type filter chips */}
          <div className="flex flex-wrap gap-1">
            {FALLBACK_ISSUE_TYPES.map((type) => {
              const cfg = resolveIssueTypeConfig(undefined, type);
              const active = filter.issueType.includes(type);
              return (
                <button
                  key={type}
                  onClick={() => toggleType(type)}
                  className={`text-[11px] px-1.5 py-0.5 rounded transition-colors ${
                    active
                      ? `${cfg?.bg ?? 'bg-gray-100'} ${cfg?.text ?? 'text-gray-700'} ring-1 ring-current`
                      : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                  }`}
                >
                  {cfg?.label ?? type}
                </button>
              );
            })}
          </div>

          {/* Priority filter chips */}
          <div className="flex flex-wrap gap-1">
            {PRIORITIES.map((p) => {
              const cfg = PRIORITY_CONFIG[p];
              const active = filter.priority.includes(p);
              return (
                <button
                  key={p}
                  onClick={() => togglePriority(p)}
                  className={`text-[11px] px-1.5 py-0.5 rounded transition-colors ${
                    active
                      ? `bg-gray-100 ${cfg?.color ?? 'text-gray-700'} ring-1 ring-current`
                      : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                  }`}
                >
                  {cfg?.label ?? p}
                </button>
              );
            })}
          </div>

          {/* Assignee filter */}
          <select
            value={filter.assigneeId}
            onChange={(e) =>
              onFilterChange({ ...filter, assigneeId: e.target.value })
            }
            className="w-full text-sm border border-gray-200 rounded px-2 py-1 text-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-300"
          >
            <option value="">담당자 전체</option>
            {members.map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.user.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Select all */}
      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-gray-100 bg-gray-50/50">
        <input
          type="checkbox"
          checked={allSelected}
          onChange={onToggleSelectAll}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
        />
        <span className="text-[11px] text-gray-500">
          {selectedIds.size > 0 ? `${selectedIds.size}건 선택` : '전체 선택'}
        </span>
      </div>

      {/* Card list */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {isLoading ? (
          <div className="flex items-center justify-center h-32 text-sm text-gray-400">
            로딩 중...
          </div>
        ) : issues.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-sm text-gray-400">
            백로그가 비어있습니다
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {issues.map((issue) => (
              <BacklogCard
                key={issue.id}
                issue={issue}
                selected={selectedIds.has(issue.id)}
                onToggle={() => onToggleSelect(issue.id)}
                onDragStart={(e) => handleDragStart(e, issue)}
                onClick={() => openDetailPanel('issue', issue.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// --- BacklogCard (inline) ---

function BacklogCard({
  issue,
  selected,
  onToggle,
  onDragStart,
  onClick,
}: {
  issue: IssueListItem;
  selected: boolean;
  onToggle: () => void;
  onDragStart: (e: React.DragEvent) => void;
  onClick: () => void;
}) {
  const typeConfig = resolveIssueTypeConfig(issue.issueTypeDef, issue.issueType);
  const priorityConfig = PRIORITY_CONFIG[issue.priority];

  return (
    <div
      draggable
      onDragStart={onDragStart}
      className={`flex items-start gap-2 px-3 py-2 cursor-grab hover:bg-gray-50 transition-colors ${
        selected ? 'bg-blue-50/50' : ''
      }`}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5 shrink-0"
      />
      <GripVertical className="w-3.5 h-3.5 text-gray-300 mt-1 shrink-0" />
      <div className="flex-1 min-w-0 cursor-pointer" onClick={onClick}>
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="text-[11px] text-gray-400 font-mono shrink-0">
            {issue.issueId}
          </span>
          {typeConfig && (
            <typeConfig.Icon size={12} className={`shrink-0 ${typeConfig.color}`} />
          )}
        </div>
        <p className="text-sm text-gray-800 line-clamp-1">{issue.title}</p>
        <div className="flex items-center gap-2 mt-1 text-[11px] text-gray-500">
          {priorityConfig && (
            <span className={priorityConfig.color}>{priorityConfig.label}</span>
          )}
          {issue.storyPoints != null && (
            <span className="bg-gray-100 text-gray-600 px-1 rounded">
              {issue.storyPoints}pt
            </span>
          )}
          {issue.assignee && (
            <span className="truncate">{issue.assignee.name}</span>
          )}
        </div>
      </div>
    </div>
  );
}
